import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldValues, Resolver } from 'react-hook-form'
import { z } from 'zod'

import {
  type Answers,
  evaluateDependency,
} from '@/modules/contato/components/ContactFormStep/index'

import { formDataAdjusted } from '@/modules/contato/formData'
import {
  type ContactApplication,
  type ContactBusinessUnit,
  type ContactFieldName,
  type ContactReason,
  contactFieldSchemas,
} from '@/modules/contato/pages/dinamicSchema/fields'

type BuildDynamicSchemaParams = {
  questionNames: readonly ContactFieldName[]
  answers?: Answers
  fallbackAnswers?: Answers
}

export function createDynamicContactSchema({
  questionNames,
  answers,
  fallbackAnswers,
}: BuildDynamicSchemaParams) {
  const resolvedAnswers: Answers = {
    ...(fallbackAnswers ?? {}),
    ...(answers ?? {}),
  }

  const shapeEntries = questionNames
    .map((questionName) => {
      const fieldSchema = contactFieldSchemas[questionName]
      if (!fieldSchema) {
        return null
      }

      const question = formDataAdjusted.questions[questionName]
      if (!question) {
        return null
      }

      const shouldInclude = evaluateDependency(
        question.dependency,
        resolvedAnswers,
      )

      if (!shouldInclude) {
        return null
      }

      return [questionName, fieldSchema] as const
    })
    .filter(
      (
        entry,
      ): entry is readonly [
        ContactFieldName,
        (typeof contactFieldSchemas)[ContactFieldName],
      ] => entry !== null,
    )

  const shape = Object.fromEntries(shapeEntries)

  return z.object(shape).superRefine((values, context) => {
    if (
      typeof values.email === 'string' &&
      typeof values.emailConfirmation === 'string' &&
      values.email !== values.emailConfirmation
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['emailConfirmation'],
        message: 'Os e-mails devem ser iguais',
      })
    }
  })
}

export type {
  ContactApplication,
  ContactBusinessUnit,
  ContactFieldName,
  ContactReason,
}

type ContactFieldSchemaMap = typeof contactFieldSchemas

export type ContactFieldValue<FieldName extends ContactFieldName> = z.infer<
  ContactFieldSchemaMap[FieldName]
>

export type ContactFieldValues = {
  [FieldName in ContactFieldName]?: ContactFieldValue<FieldName>
}

type CreateDynamicContactResolverParams<FormValues extends FieldValues> = {
  questionNames: readonly ContactFieldName[]
  fallbackAnswers?: Answers
}

export function createDynamicContactResolver<FormValues extends FieldValues>({
  questionNames,
  fallbackAnswers,
}: CreateDynamicContactResolverParams<FormValues>): Resolver<FormValues> {
  return async (values, context, options) => {
    const schema = createDynamicContactSchema({
      questionNames,
      answers: values as unknown as Answers,
      fallbackAnswers,
    })

    const resolver = zodResolver(schema)

    return resolver(values, context, options)
  }
}
